import React from 'react'
import Modal from '@/components/Modal'
import { useLanguage } from '@/contexts/LanguageContext'
import { getRoomDisplayName, getMonthName } from '../helpers'
import type { useTurbineDetailViewModel } from '../useTurbineDetailViewModel'

type VM = ReturnType<typeof useTurbineDetailViewModel>

interface PhotoComparisonModalProps {
  selectedPhotosForComparison: VM['selectedPhotosForComparison']
  showComparisonModal: VM['showComparisonModal']
  setShowComparisonModal: VM['setShowComparisonModal']
}

/**
 * Iki fotografi yan yana gosterir.
 *
 * Ortak `Modal` tabanina tasindi: beyaz panelli klasik bir dialog oldugu icin taban
 * gorunumune uyuyor. `panelClassName` ile onceki genislik/padding/radius korundu.
 * Kazanim: tek z-index merdiveni, calisan ESC, arka plan kaydirma kilidi.
 * Bu modalin kendi hata durumu yok (salt gosterim), bu yuzden `error` verilmiyor.
 */
const PhotoComparisonModal: React.FC<PhotoComparisonModalProps> = ({
  selectedPhotosForComparison,
  showComparisonModal,
  setShowComparisonModal,
}) => {
  const { t, language } = useLanguage()

  return (
    <Modal
      isOpen={showComparisonModal && selectedPhotosForComparison.length === 2}
      onClose={() => setShowComparisonModal(false)}
      title="Fotoğraf Karşılaştırma"
      size="xl"
      panelClassName="!max-w-6xl !p-6 !rounded-lg"
      footer={
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setShowComparisonModal(false)}
            className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
          >
            {t.common.close}
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {selectedPhotosForComparison.map((photo, index) => (
          <div key={photo.key} className="space-y-3">
            <div className="text-center">
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                Fotoğraf {index + 1}
              </h4>
              <p className="text-sm text-gray-600">
                {photo.originalName || (photo.key ? photo.key.split('/').pop()?.split('-').slice(2).join('-') || 'İsimsiz Fotoğraf' : 'İsimsiz Fotoğraf')}
              </p>
            </div>

            <div className="bg-gray-100 rounded-lg overflow-hidden">
              {photo.key && photo.key.toLowerCase().endsWith('.pcd') ? (
                <div className="w-full h-80 flex items-center justify-center bg-gray-200">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-gray-700">PCD Dosyası</p>
                    <p className="text-xs text-gray-500 mt-1">360° görüntü için özel viewer gerekli</p>
                  </div>
                </div>
              ) : (
                <img
                  src={photo.url}
                  alt={photo.originalName || `Fotoğraf ${index + 1}`}
                  className="w-full h-80 object-contain"
                  loading="lazy"
                />
              )}
            </div>

            <div className="text-sm text-gray-500 space-y-1">
              <p><span className="font-medium">{t.turbineDetail.rooms.room}:</span> {photo.room ? getRoomDisplayName(parseInt(photo.room.toString())) : (language === 'tr' ? 'Bilinmiyor' : 'Unknown')}</p>
              {photo.year && <p><span className="font-medium">{language === 'tr' ? 'Tarih:' : 'Date:'}</span> {photo.month ? getMonthName(photo.month, language) : ''} {photo.year}</p>}
              {photo.size && (
                <p><span className="font-medium">{language === 'tr' ? 'Boyut:' : 'Size:'}</span> {(photo.size / 1024 / 1024).toFixed(1)} MB</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  )
}

export default PhotoComparisonModal
